import { useEffect, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@london-life/ui/components/dialog';
import { formatPence, type NeighbourView, type PlayState, type TravelMode } from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { PersonPicture } from '../people/person-card';
import { useTravelChoices } from '../lib/travel';
import { api } from '../lib/api';

const nameOf = (person: { username: string | null; firstName: string }) =>
  person.username ? `@${person.username}` : person.firstName;

// Lagos Life's Neighbours: the homes you can drop by when they're in (those at home first), each with its kind,
// area and how much is in it. Pick one, choose how to get there, and go: you knock when you arrive, or walk
// straight in if they invited you over.
export function NeighboursSheet({
  open,
  onClose,
  hostLifeId,
  onVisit,
  car,
}: {
  open: boolean;
  onClose: () => void;
  // opened for one home (an invite card's "Go over")
  hostLifeId?: string | null;
  onVisit: (host: NeighbourView, mode: TravelMode) => Promise<boolean>;
  // the player's car, for "Car" among the ways there
  car?: PlayState['car'];
}) {
  const [list, setList] = useState<NeighbourView[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [picked, setPicked] = useState<NeighbourView | null>(null);
  const [mode, setMode] = useState<TravelMode>('walk');
  const [going, setGoing] = useState(false);
  useEffect(() => {
    if (!open) {
      setPicked(null);
      return;
    }
    setFailure(null);
    setList(null);
    let live = true;
    api.visits
      .neighbours()
      .then((reply) => {
        if (!live) return;
        setList(reply.neighbours);
        if (hostLifeId) setPicked(reply.neighbours.find((item) => item.lifeId === hostLifeId) ?? null);
      })
      .catch((error: unknown) => live && setFailure(describeError(error).message));
    return () => {
      live = false;
    };
  }, [open, hostLifeId]);
  const travel = useTravelChoices(car);
  const fare = travel.find((option) => option.mode === mode) ?? travel[0];
  const go = async () => {
    if (!picked || !fare) return;
    setGoing(true);
    const ok = await onVisit(picked, fare.mode);
    setGoing(false);
    if (ok) onClose();
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="city-card city-phone neighbours-sheet">
        {picked ? (
          <>
            <button type="button" className="neighbours-back" onClick={() => setPicked(null)}>
              <ChevronLeft size={16} /> Neighbours
            </button>
            <div className="neighbour-head">
              <PersonPicture person={picked} />
              <div>
                <DialogTitle>{nameOf(picked)}&rsquo;s place</DialogTitle>
                <DialogDescription>
                  {picked.home.label} · {picked.home.area} ·{' '}
                  {picked.atHome ? "they're home 🟢" : "they're out right now"}
                </DialogDescription>
              </div>
            </div>
            <p className="neighbours-note">
              {picked.invited
                ? 'They invited you over: you walk straight in. Be nice to their things!'
                : 'You knock when you get there, and they decide whether to let you in.'}
            </p>
            <div className="city-card-transport" role="radiogroup" aria-label="How to get there">
              {travel.map((item) => (
                <button
                  type="button"
                  key={item.mode}
                  role="radio"
                  aria-checked={fare?.mode === item.mode}
                  className={fare?.mode === item.mode ? 'active' : ''}
                  onClick={() => setMode(item.mode)}
                >
                  <span>{item.icon}</span>
                  <strong>{item.label}</strong>
                  <small>{item.farePence ? formatPence(item.farePence) : (item.note ?? 'Free')}</small>
                </button>
              ))}
            </div>
            <button type="button" className="city-card-go" disabled={!picked.atHome || going} onClick={() => void go()}>
              {!picked.atHome
                ? `${nameOf(picked)} is out`
                : `${picked.invited ? 'Go over' : 'Go and knock'} · ${fare?.farePence ? formatPence(fare.farePence) : 'Free'}`}
            </button>
          </>
        ) : (
          <>
            <DialogTitle>🏘️ Neighbours</DialogTitle>
            <DialogDescription>You can drop by when they&rsquo;re home.</DialogDescription>
            {failure ? (
              <p className="neighbours-note">{failure}</p>
            ) : !list ? (
              <p className="neighbours-note">Knocking on doors…</p>
            ) : !list.length ? (
              <p className="neighbours-note">No other players yet. Invite your friends to London Life!</p>
            ) : (
              <ul className="neighbours-list">
                {list.map((item) => (
                  <li key={item.lifeId}>
                    <button
                      type="button"
                      disabled={!item.atHome}
                      aria-label={item.atHome ? `Visit ${nameOf(item)}` : `${nameOf(item)} is not home`}
                      onClick={() => setPicked(item)}
                    >
                      <span className="neighbour-picture">
                        <PersonPicture person={item} />
                        {item.online && <i aria-label="Online" />}
                      </span>
                      <span className="neighbour-who">
                        <strong>
                          {nameOf(item)}
                          {item.invited && <em>Invited you</em>}
                        </strong>
                        <small>
                          {item.home.label} · {item.home.area} · {item.items} {item.items === 1 ? 'thing' : 'things'}
                        </small>
                      </span>
                      <span className={item.atHome ? 'neighbour-go' : 'neighbour-out'}>
                        {item.atHome ? 'Visit' : item.online ? 'Out' : 'Away'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
